import { Component, Inject, OnDestroy, OnInit, ElementRef, Renderer2, DOCUMENT, ChangeDetectorRef } from '@angular/core';

import { NavigationEnd, RouteConfigLoadStart, Router } from '@angular/router';
import { Title } from '@angular/platform-browser';

import { Subject } from 'rxjs';
import { filter, switchMap, take, takeUntil } from 'rxjs/operators';
import { TranslateService } from '@ngx-translate/core';
import * as Waves from 'node-waves';

import { MsalBroadcastService, MsalGuardConfiguration, MsalService, MSAL_GUARD_CONFIG } from '@azure/msal-angular';
import { AuthenticationResult, EventMessage, EventType, InteractionStatus, RedirectRequest, SilentRequest, SsoSilentRequest } from '@azure/msal-browser';

import { app, authentication, pages } from "@microsoft/teams-js";

import { CoreSidebarService } from '@core/components/core-sidebar/core-sidebar.service';
import { CoreConfigService } from '@core/services/config.service';
import { CoreLoadingScreenService } from '@core/services/loading-screen.service';
import { checkInTeams } from '@core/utils';

import { AuthenticationService } from '@common/services';

import { CustomSwalService } from './helpers';
import { environment } from 'environments/environment';
import { IdleTimeoutService, InsightsService } from '@core/services';
import { authContext, authError, resetAutoRedirect, takeRedirectReturnUrl, tryAutoRedirect } from './msal-config';

@Component({
    selector: 'app-root',
    templateUrl: './app.component.html',
    styleUrls: ['./app.component.scss'],
    standalone: false
})
export class AppComponent implements OnInit, OnDestroy {
  coreConfig: any;
  menu: any;

  // Private
  private _unsubscribeAll: Subject<any>;
  private navigating: boolean = false;

  /**
   * Constructor
   *
   * @param {DOCUMENT} document
   * @param {Title} _title
   * @param {Renderer2} _renderer
   * @param {ElementRef} _elementRef
   * @param {CoreConfigService} _coreConfigService
   * @param {CoreSidebarService} _coreSidebarService
   * @param {CoreLoadingScreenService} _coreLoadingScreenService
   * @param {CoreMenuService} _coreMenuService
   * @param {TranslateService} _translateService
   */
  constructor(
    @Inject(DOCUMENT) private document: any,
    private _title: Title,
    private _renderer: Renderer2,
    private _elementRef: ElementRef,
    public _coreConfigService: CoreConfigService,
    private _coreSidebarService: CoreSidebarService,
    private _coreLoadingScreenService: CoreLoadingScreenService,
    private _translateService: TranslateService,
    private _authenticationService: AuthenticationService,
    private _broadcastService: MsalBroadcastService, 
    private _azureAuthService: MsalService,
    private router: Router,
    private _swalService: CustomSwalService,
    private _idleTimeoutService: IdleTimeoutService,
    private _insightsService: InsightsService,
    private _cdkChangeDetectorRef: ChangeDetectorRef,
    @Inject(MSAL_GUARD_CONFIG) private msalGuardConfig: MsalGuardConfiguration
  ) {

    // Add languages to the translation service
    this._translateService.addLangs(['en', 'es']);

    // This language will be used as a fallback when a translation isn't found in the current language
    this._translateService.setDefaultLang('es');

    // Initialize coreConfig from defaultConfig to prevent *ngIf errors on first render
    this.coreConfig = this._coreConfigService.defaultConfig;

    // Set the private defaults
    this._unsubscribeAll = new Subject();
  }

  // Lifecycle hooks
  // -----------------------------------------------------------------------------------------------------

  /**
   * On init
   */
  ngOnInit(): void {
    // Init wave effect (Ripple effect)
    Waves.init();

    // Las traducciones ya fueron cargadas por APP_INITIALIZER
    this.loadLayoutComponents();

    if(checkInTeams()){
      app.initialize().then(() => {
          app.getContext().then((context) => {
            console.log('Contexto de Teams:', context);
          });
      });
    }

    // Loading page
    this.router.events.pipe(takeUntil(this._unsubscribeAll)).subscribe(event => {
      if(event instanceof RouteConfigLoadStart){
        if(this.navigating == false){
          this._swalService.showLoading();
          this.navigating = true;
        }
      }else if(event instanceof NavigationEnd){
        this._swalService.close();
        this.navigating = false;
      }
    });

    // Si el usuario esta autenticado localmente, registra permisos directamente
    if(this._authenticationService.isAuthenticated() && !this._authenticationService.isTokenExpired() && localStorage.getItem('auth-type') === 'local'){
      this._authenticationService.registerPermissionsAndStatus();
    } else {
      this.setLoginAzureUser();
    }

    // this._azureAuthService.instance.enableAccountStorageEvents(); // Optional - This will enable ACCOUNT_ADDED and ACCOUNT_REMOVED events emitted when a user logs in or out of another tab or window

    this._broadcastService.msalSubject$
    .pipe(
      filter((msg: EventMessage) =>
        msg.eventType === EventType.LOGIN_SUCCESS ||
        msg.eventType === EventType.LOGOUT_SUCCESS
      ),
      takeUntil(this._unsubscribeAll)
    )
    .subscribe((result: EventMessage) => {
      if (this._azureAuthService.instance.getAllAccounts().length === 0) {
        window.location.pathname = "/";
      }
    });

    // Se espera a que MSAL termine de procesar el redirect antes de verificar cuentas
    this._broadcastService.inProgress$
    .pipe(
      filter((status: InteractionStatus) => status === InteractionStatus.None),
      takeUntil(this._unsubscribeAll)
    )
    .subscribe(() => {
      this.checkAndSetActiveAccount();
    });

    // Para capturar el evento cuando se vencerá la sesión del token
    this.onTokenTimeoutExpired();
  }

  /**
   * On destroy
   */
  ngOnDestroy(): void {
    // Unsubscribe from all subscriptions
    this._unsubscribeAll.next(null);
    this._unsubscribeAll.complete();
  }

  // Public methods
  // -----------------------------------------------------------------------------------------------------

  /**
   * Toggle sidebar open
   *
   * @param key
   */
  toggleSidebar(key): void {
    this._coreSidebarService.getSidebarRegistry(key).toggleOpen();
  }

  checkAndSetActiveAccount(){
    /**
     * If no active account set but there are accounts signed in, sets first account to active account
     * To use active account set here, subscribe to inProgress$ first in your component
     * Note: Basic usage demonstrated. Your app may require more complicated account selection logic
     */
    let activeAccount = this._azureAuthService.instance.getActiveAccount();

    if (!activeAccount && this._azureAuthService.instance.getAllAccounts().length > 0) {
      let accounts = this._azureAuthService.instance.getAllAccounts();
      this._azureAuthService.instance.setActiveAccount(accounts[0]);
    }
  }

  setLoginAzureUser(){
    if (this._azureAuthService.instance.getAllAccounts().length > 0){
      // Se avisa que la sesión se está renovando para que las peticiones que hagan
      // las pantallas esperen al token nuevo en lugar de salir con el token viejo
      this._authenticationService.beginSessionRenewal();

      this._azureAuthService.acquireTokenSilent({...this.msalGuardConfig.authRequest, forceRefresh: true} as SilentRequest)
        .pipe(takeUntil(this._unsubscribeAll))
        .subscribe(
          (response) => {
            this.login(response);
          },
          (error) => {
            this._authenticationService.endSessionRenewal();

            // Un redirect a Entra resuelve casi cualquier fallo del silencioso
            // (en Safari el iframe de respaldo nunca funciona), pero solo una
            // vez: si recién se volvió de Entra y vuelve a fallar, repetirlo
            // deja al usuario recargando sin fin
            if (tryAutoRedirect()) {
              this._insightsService.traceError(
                environment.projects.Login,
                'No se pudo renovar el token en silencio, se redirige a Entra ID',
                authContext(), authError(error), null
              );
              this._azureAuthService.acquireTokenRedirect({...this.msalGuardConfig.authRequest} as RedirectRequest);
              return;
            }

            this._insightsService.traceError(
              environment.projects.Login,
              'Se frenó un bucle de redirects a Entra ID: el token no se pudo renovar al volver',
              authContext(), authError(error), null
            );

            // Se corta el bucle: sesión local limpia, al login y con aviso.
            // El usuario puede volver a intentarlo con el botón de Microsoft
            this._authenticationService.logout();
            this.navigateToLoginPage();
            this._swalService.showWarningMsg(this._translateService.instant('PAGES.LOGIN.MESSAGES.RENEW_FAILED'));
          }
      );
    } else {
      this.navigateToLoginPage();
    }
  }

  loadLayoutComponents(){
    // Subscribe to config changes
    this._coreConfigService.config.pipe(takeUntil(this._unsubscribeAll)).subscribe(config => {
      this.coreConfig = config;

      // Layout
      //--------

      // Remove default classes first
      this._elementRef.nativeElement.classList.remove(
        'vertical-layout',
        'vertical-menu-modern',
        'horizontal-layout',
        'horizontal-menu',
        'home-layout-page'
      );
      // Add class based on config options
      if (this.coreConfig.layout.type === 'vertical') {
        this._elementRef.nativeElement.classList.add('vertical-layout', 'vertical-menu-modern');
      } else if (this.coreConfig.layout.type === 'horizontal') {
        this._elementRef.nativeElement.classList.add('horizontal-layout', 'horizontal-menu');
      } else if (this.coreConfig.layout.type === 'home-layout') {
        this._elementRef.nativeElement.classList.add('home-layout-page');
      }

      // Navbar
      //--------

      // Remove default classes first
      this._elementRef.nativeElement.classList.remove(
        'navbar-floating',
        'navbar-static',
        'navbar-sticky',
        'navbar-hidden'
      );

      // Add class based on config options
      if (this.coreConfig.layout.navbar.type === 'navbar-static-top') {
        this._elementRef.nativeElement.classList.add('navbar-static');
      } else if (this.coreConfig.layout.navbar.type === 'fixed-top') {
        this._elementRef.nativeElement.classList.add('navbar-sticky');
      } else if (this.coreConfig.layout.navbar.type === 'floating-nav') {
        this._elementRef.nativeElement.classList.add('navbar-floating');
      } else {
        this._elementRef.nativeElement.classList.add('navbar-hidden');
      }

      // Footer
      //--------

      // Remove default classes first
      this._elementRef.nativeElement.classList.remove('footer-fixed', 'footer-static', 'footer-hidden');

      // Add class based on config options
      if (this.coreConfig.layout.footer.type === 'footer-sticky') {
        this._elementRef.nativeElement.classList.add('footer-fixed');
      } else if (this.coreConfig.layout.footer.type === 'footer-static') {
        this._elementRef.nativeElement.classList.add('footer-static');
      } else {
        this._elementRef.nativeElement.classList.add('footer-hidden');
      }

      // Blank layout
      if (
        this.coreConfig.layout.menu.hidden &&
        this.coreConfig.layout.navbar.hidden &&
        this.coreConfig.layout.footer.hidden
      ) {
        this._elementRef.nativeElement.classList.add('blank-page');
        // ! Fix: Transition issue while coming from blank page
        const appContent = this._elementRef.nativeElement.getElementsByClassName('app-content')[0];
        if (appContent) {
          this._renderer.setAttribute(appContent, 'style', 'transition:none');
        }
      } else {
        this._elementRef.nativeElement.classList.remove('blank-page');
        // ! Fix: Transition issue while coming from blank page
        setTimeout(() => {
          const appContent = this._elementRef.nativeElement.getElementsByClassName('app-content')[0];
          if (appContent) {
            this._renderer.setAttribute(appContent, 'style', 'transition:300ms ease all');
          }
        }, 0);
        // If navbar hidden
        if (this.coreConfig.layout.navbar.hidden) {
          this._elementRef.nativeElement.classList.add('navbar-hidden');
        }
        // Menu (Vertical menu hidden)
        if (this.coreConfig.layout.menu.hidden) {
          this._renderer.setAttribute(this._elementRef.nativeElement, 'data-col', '1-column');
        } else {
          this._renderer.removeAttribute(this._elementRef.nativeElement, 'data-col');
        }
        // Footer
        if (this.coreConfig.layout.footer.hidden) {
          this._elementRef.nativeElement.classList.add('footer-hidden');
        }
      }

      // Skin Class (Adding to body as it requires highest priority)
      if (this.coreConfig.layout.skin !== '' && this.coreConfig.layout.skin !== undefined) {
        this.document.body.classList.remove('default-layout', 'bordered-layout', 'dark-layout', 'semi-dark-layout');
        this.document.body.classList.add(this.coreConfig.layout.skin + '-layout');
      }
    });

    // Set the application page title
    this._title.setTitle(this.coreConfig.app.appTitle);

    this._cdkChangeDetectorRef.detectChanges();
  }

  login(response: AuthenticationResult): void {
    // La sesión quedó bien: el próximo fallo vuelve a tener derecho a un redirect
    resetAutoRedirect();

    // El token se guarda antes de cerrar la renovación para que las peticiones
    // retenidas salgan ya con el token nuevo
    this._authenticationService.saveUserToken(response.accessToken, 'saml');
    this._authenticationService.endSessionRenewal();
    this._azureAuthService.instance.setActiveAccount(response.account);
    this._swalService.close();
    this.loadLayoutComponents();

    // Si se viene de Entra, recién ahora se lleva al usuario a donde iba
    const returnUrl = takeRedirectReturnUrl();
    if (returnUrl) {
      this._insightsService.traceInfo(environment.projects.Login, 'Navega al destino tras iniciar sesión con Entra ID', { ...authContext(), returnUrl }, null);
      this.router.navigateByUrl(returnUrl);
    }
  }

  /**
   * Función que se ejecuta cuando expira el timeout
   */
  private onTokenTimeoutExpired(): void {
    this._idleTimeoutService.timeoutExpired.pipe(
      switchMap(() => this._translateService.get(['PAGES.LOGIN.SESSION_EXPIRED.MESSAGE_TITLE', 'PAGES.LOGIN.SESSION_EXPIRED.MESSAGE'])),
      takeUntil(this._unsubscribeAll)
    ).subscribe(value => {
      this._swalService.showQuestion(value['PAGES.LOGIN.SESSION_EXPIRED.MESSAGE_TITLE'], value['PAGES.LOGIN.SESSION_EXPIRED.MESSAGE'])
        .then(result => {
          this._swalService.showLoading();

          // Se detiene el timeout service
          this._idleTimeoutService.stopTimer();

          // Condicional para validar la respuesta del usuario
          if (result.isConfirmed) {
            // Se obtiene el token de manera silenciosa
            this.setLoginAzureUser();
          } else {
            // Se cierra la sesión
            this._authenticationService.logout();
            this.navigateToLoginPage();
          }
        });
    });
  }

  private navigateToLoginPage(): void {
    // Si ya estamos en la página de login no navegar para evitar returnUrl anidado
    if (window.location.pathname.startsWith('/pages/authentication/login')) {
      return;
    }
    const returnUrl = window.location.pathname + window.location.search;
    this.router.navigate(['/pages/authentication/login'], {
      queryParams: { returnUrl }
    });
  }

}
